/**
 * User Management Service
 * Handles SUPER_ADMIN user management operations across all shops
 */

import { apiClient } from './client';
import { getConfig, createLogger } from '../config/environment';

const logger = createLogger('UserManagementService');
const config = getConfig();

export interface ShopUser {
  id: string;
  userId: string;
  role: 'ADMIN' | 'STAFF';
  email?: string;
  mobile?: string;
  mobileCountryCode?: string;
  status?: 'ACTIVE' | 'INACTIVE';
  createdAt?: string;
  lastLogin?: string;
}

export interface ShopUsersResponse {
  status: string;
  message: string;
  shopId: string;
  count: number;
  users: ShopUser[];
}

export interface ResetPasswordRequest {
  newPassword: string;
  confirmPassword: string;
}

export interface ResetPasswordResponse {
  status: string;
  message: string;
  userId: string;
}

export interface DeleteUserResponse {
  status: string;
  message: string;
  userId: string;
  deletedRole?: string;
}

/**
 * User Management Service Class
 */
export class UserManagementService {
  /**
   * Get all users for a specific shop
   * GET /api/auth/{shopId}/users
   *
   * @param shopId - Shop ID to get users for
   * @returns Promise with shop users
   */
  async getShopUsers(shopId: string): Promise<ShopUser[]> {
    try {
      logger.info('Fetching users for shop:', shopId);

      const response = await apiClient.get<ShopUsersResponse>(`/api/auth/${shopId}/users`);

      if (response.status !== 200) {
        throw new Error('Failed to fetch shop users');
      }

      const data = response.data;

      if (data.status !== 'success') {
        throw new Error(data.message || 'Failed to fetch shop users');
      }

      logger.info('✅ Shop users fetched successfully', {
        shopId,
        userCount: data.count
      });

      return data.users;
    } catch (error) {
      logger.error('Failed to fetch shop users', error);
      throw error;
    }
  }

  /**
   * Delete a user (STAFF only)
   * DELETE /api/auth/users/{userId}
   *
   * @param userId - User ID to delete
   * @returns Promise with deletion result
   */
  async deleteUser(userId: string): Promise<DeleteUserResponse> {
    try {
      logger.info('Deleting user:', userId);

      const response = await apiClient.delete<DeleteUserResponse>(`/api/auth/users/${userId}`);

      if (response.status !== 200) {
        throw new Error('Failed to delete user');
      }

      const data = response.data;

      if (data.status !== 'success') {
        throw new Error(data.message || 'Failed to delete user');
      }

      logger.info('✅ User deleted successfully', {
        userId,
        deletedRole: data.deletedRole
      });

      return data;
    } catch (error) {
      logger.error('Failed to delete user', error);
      throw error;
    }
  }

  /**
   * Reset user password
   * POST /api/auth/user/{userId}/reset-password
   *
   * @param userId - User ID to reset password for
   * @param request - Reset password request with new password
   * @returns Promise with reset result
   */
  async resetUserPassword(userId: string, request: ResetPasswordRequest): Promise<ResetPasswordResponse> {
    try {
      logger.info('Resetting password for user:', userId);

      const response = await apiClient.post<ResetPasswordResponse>(
        `/api/auth/user/${userId}/reset-password`,
        request
      );

      if (response.status !== 200) {
        throw new Error('Failed to reset user password');
      }

      const data = response.data;

      if (data.status !== 'success') {
        throw new Error(data.message || 'Failed to reset user password');
      }

      logger.info('✅ User password reset successfully', {
        userId
      });

      return data;
    } catch (error) {
      logger.error('Failed to reset user password', error);
      throw error;
    }
  }
}

/**
 * Singleton instance
 */
let userManagementServiceInstance: UserManagementService | null = null;

/**
 * Get user management service instance
 */
export const getUserManagementService = (): UserManagementService => {
  if (!userManagementServiceInstance) {
    userManagementServiceInstance = new UserManagementService();
  }
  return userManagementServiceInstance;
};

/**
 * Reset user management service instance (for testing)
 */
export const resetUserManagementService = (): void => {
  userManagementServiceInstance = null;
};

/**
 * Default export instance
 */
export const userManagementService = getUserManagementService();
